import * as React from "react";
import { CheckCircle, AlertCircle, AlertTriangle, ChevronDown, ChevronUp } from "react-feather";
import { ProcessedWorkload } from "../../../store/healthSlice";
import { HEALTH_COLORS } from "../utils";
import { Gvc } from "../../../schema/types/gvc/entity";
import { InternalLink } from "../../../newcomponents/table/components/InternalLink";
import { linksOf } from "../../../services/cpln";

interface ProcessedGvcData {
  item: Gvc;
  workloads: {
    ready: ProcessedWorkload[];
    notReady: ProcessedWorkload[];
    notLatest: ProcessedWorkload[];
    syncFailed: ProcessedWorkload[];
    suspended: ProcessedWorkload[];
    partiallySuspended: ProcessedWorkload[];
  };
}

interface GvcHealthCardProps {
  gvc: ProcessedGvcData;
  isExpanded: boolean;
  onToggle: () => void;
}

export const GvcHealthCard: React.FC<GvcHealthCardProps> = ({ gvc, isExpanded, onToggle }) => {
  const totalWorkloads =
    gvc.workloads.ready.length +
    gvc.workloads.notReady.length +
    gvc.workloads.notLatest.length +
    gvc.workloads.syncFailed.length +
    gvc.workloads.suspended.length +
    gvc.workloads.partiallySuspended.length;

  const healthyCount =
    gvc.workloads.ready.length + gvc.workloads.suspended.length + gvc.workloads.partiallySuspended.length;

  const warningCount = gvc.workloads.notLatest.length;
  const errorCount = gvc.workloads.notReady.length + gvc.workloads.syncFailed.length;

  const healthPercentage = totalWorkloads > 0 ? Math.round((healthyCount / totalWorkloads) * 100) : 100;
  const warningPercentage = totalWorkloads > 0 ? Math.round((warningCount / totalWorkloads) * 100) : 0;
  const errorPercentage = totalWorkloads > 0 ? Math.round((errorCount / totalWorkloads) * 100) : 0;

  const hasIssues = errorCount > 0 || warningCount > 0;

  const getStatusIcon = () => {
    if (errorCount > 0) return <AlertCircle size={16} color={HEALTH_COLORS.error} />;
    if (warningCount > 0) return <AlertTriangle size={16} color={HEALTH_COLORS.warning} />;
    return <CheckCircle size={16} color={HEALTH_COLORS.ready} />;
  };

  const getStatusText = () => {
    if (errorCount > 0) return `${errorCount} error${errorCount > 1 ? "s" : ""}`;
    if (warningCount > 0) return `${warningCount} warning${warningCount > 1 ? "s" : ""}`;
    return "All healthy";
  };

  const selfLink = linksOf(gvc.item).self!;

  return (
    <div
      className={`rounded-lg p-4 overflow-hidden transition-all ${hasIssues && !isExpanded ? "cursor-pointer hover:shadow-md" : ""}`}
      style={{
        backgroundColor: "var(--health-card-bg)",
        border: "1px solid var(--color-border)",
        gridColumn: isExpanded ? "span 4" : "span 1",
      }}
      onClick={hasIssues && !isExpanded ? onToggle : undefined}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2 gap-2" style={{ minWidth: 0 }}>
        <div
          className="min-w-0 flex-1"
          style={{
            overflow: isExpanded ? "visible" : "hidden",
            textOverflow: "ellipsis",
            whiteSpace: isExpanded ? "normal" : "nowrap",
            display: "block",
          }}
          title={gvc.item.name}
        >
          <span className="text-sm font-medium" style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}>
            <InternalLink label={gvc.item.name} to={`/console${selfLink}`} onClick={(e) => e.stopPropagation()} />
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {getStatusIcon()}
          {isExpanded && (
            <button
              onClick={onToggle}
              className="p-1 rounded hover:bg-opacity-10 transition-colors"
              style={{ color: "var(--color-label)" }}
            >
              <ChevronUp size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center justify-between text-xs">
        <span style={{ color: "var(--color-label)" }}>
          {healthyCount}/{totalWorkloads} ready
        </span>
        <span
          style={{
            color:
              errorCount > 0 ? HEALTH_COLORS.error : warningCount > 0 ? HEALTH_COLORS.warning : HEALTH_COLORS.ready,
          }}
        >
          {getStatusText()}
        </span>
      </div>

      {/* Expand hint for collapsed cards with issues */}
      {!isExpanded && hasIssues && (
        <div className="flex items-center justify-center mt-2" style={{ color: "var(--color-label)" }}>
          <ChevronDown size={14} />
        </div>
      )}

      {/* Expanded Content - Problematic Workloads */}
      {isExpanded && hasIssues && (
        <div className="mt-4 pt-4" style={{ borderTop: "1px dashed var(--color-border)" }}>
          {/* Sync Failed */}
          {gvc.workloads.syncFailed.length > 0 && (
            <WorkloadGroup title="Sync Failed" workloads={gvc.workloads.syncFailed} color={HEALTH_COLORS.error} />
          )}

          {/* Not Ready */}
          {gvc.workloads.notReady.length > 0 && (
            <WorkloadGroup title="Not Ready" workloads={gvc.workloads.notReady} color={HEALTH_COLORS.error} />
          )}

          {/* Not Latest */}
          {gvc.workloads.notLatest.length > 0 && (
            <WorkloadGroup title="Not Latest Version" workloads={gvc.workloads.notLatest} color={HEALTH_COLORS.warning} />
          )}
        </div>
      )}
    </div>
  );
};

interface WorkloadGroupProps {
  title: string;
  workloads: ProcessedWorkload[];
  color: string;
}

const WorkloadGroup: React.FC<WorkloadGroupProps> = ({ title, workloads, color }) => {
  return (
    <div className="mb-4 last:mb-0">
      <div className="text-xs font-medium mb-2" style={{ color }}>
        {title} ({workloads.length})
      </div>
      <div className="flex flex-col gap-2">
        {workloads.map((workload) => {
          const message = workload.messages[0];
          return (
            <div
              key={workload.selfLink}
              className="rounded p-2 text-xs"
              style={{ backgroundColor: "var(--color-border)" }}
            >
              <div className="flex items-center justify-between mb-1 gap-2">
                <div className="truncate min-w-0 flex-1">
                  <InternalLink label={workload.item.name} to={`/console${workload.selfLink}`} />
                </div>
                {message?.location && (
                  <span
                    className="text-xs flex-shrink-0"
                    style={{ color: "var(--color-label)" }}
                  >
                    {message.location}
                  </span>
                )}
              </div>
              {message?.text && (
                <div className="truncate" style={{ color: "var(--color-label)" }} title={message.text}>
                  {message.text}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
